import { nextOrient, bearingForOrient } from "./orient_cycle"
import { createCompassWatcher, compassSupported } from "./compass"
import { createCameraIntent } from "./camera_intent"
import { CAMERA_TIMING_POLICY } from "./camera_timing_policy"
import { cameraModeReducer } from "./camera_mode"
import { FOLLOW_PITCH_MAX, gestureAdjustment, panDistancePx } from "./camera_gesture"
import {
  followButtonState,
  liveButtonState,
  orientButtonState,
  pitchButtonState,
  pitchTogglePlan
} from "./map_control_state"
import { WEB_DEFAULT_SETTINGS } from "./web_settings"
import { browserLocationWatchOptions, browserLocationLocateOptions } from "./web_location_policy"
import { browserFollowCameraPlan, trackingFollowCameraPlan } from "./web_follow_camera"
import { webOrientationCameraPlan } from "./web_orientation_camera"

// Owns the map camera's state machine and sensor lifecycle. The surrounding
// Stimulus controller remains responsible for layers, persistence, and HUD
// data; this component exposes explicit commands and reports location/settings
// changes through injected callbacks.
export class CameraController {
  static CAMERA_CORRECTIONS = CAMERA_TIMING_POLICY.correctionDelaysMs
  static HARD_CAMERA_CORRECTIONS = CAMERA_TIMING_POLICY.hardCorrectionDelaysMs

  constructor({
    map,
    controls = {},
    getSetting = key => WEB_DEFAULT_SETTINGS[key],
    onSettingsChange = () => {},
    onLocation = () => {},
    onPanMove = () => {},
    onZoomMove = () => {},
    onMoveEnd = () => {},
    onForceAutoSearch = () => {},
    autoSearchEnabled = () => false,
    search = () => {},
    setStatus = () => {},
    showLocate = () => {},
    isConnected = () => true,
    geolocation = typeof navigator !== "undefined" ? navigator.geolocation : null,
    compassFactory = createCompassWatcher,
    compassAvailable = compassSupported,
    intentFactory = createCameraIntent
  }) {
    this.map = map
    this.controls = controls
    this.getSetting = getSetting
    this.onSettingsChange = onSettingsChange
    this.onLocation = onLocation
    this.onPanMove = onPanMove
    this.onZoomMove = onZoomMove
    this.onMoveEnd = onMoveEnd
    this.onForceAutoSearch = onForceAutoSearch
    this.autoSearchEnabled = autoSearchEnabled
    this.search = search
    this.setStatus = setStatus
    this.showLocate = showLocate
    this.isConnected = isConnected
    this.geolocation = geolocation
    this.compassAvailable = compassAvailable

    this.mode = "liveIdle"
    this.tracking = false
    this.orientation = "north"
    this.last = null
    this.course = null
    this.compassHeading = null
    this.pitch3d = false
    this.zoomedToLive = false
    this.zoomedToFollow = false
    this.listeners = []
    this.cameraIntent = intentFactory(() => this.map, { onCommand: () => { this.programmatic = true } })
    this.compass = compassFactory(heading => this.#onCompassHeading(heading))
  }

  start() {
    const pitch = this.#followPitch()
    this.pitch3d = pitch > 0
    this.map.jumpTo({ pitch })
    this.#listen("dragstart", () => {
      this.onPanMove()
      this.zoomGesture = null
      this.dragGesture = this.#gestureSnapshot()
      this.#userMoved()
    })
    this.#listen("dragend", () => {
      this.#classifyGestureEnd(this.dragGesture)
      this.dragGesture = null
    })
    this.#listen("zoomstart", event => {
      this.onZoomMove()
      if (event.originalEvent || !this.programmatic) {
        if (event.originalEvent) this.zoomGesture = this.#gestureSnapshot()
        this.#userMoved()
      }
    })
    this.#listen("zoomend", () => {
      this.#classifyGestureEnd(this.zoomGesture)
      this.zoomGesture = null
    })
    this.#listen("moveend", () => {
      this.programmatic = false
      this.onMoveEnd()
    })
    this.#listen("rotatestart", event => {
      if (!event.originalEvent) return
      this.#cancelCameraIntent()
      if (this.orientation !== "off") {
        this.orientation = "off"
        this.#renderOrientButton()
      }
    })
    this.#listen("pitchend", event => {
      this.pitch3d = this.map.getPitch() > 0
      this.#renderPitchButton()
      if (event.originalEvent && this.isLive() && this.pitch3d) {
        this.#saveCameraSetting("followPitch", Math.min(FOLLOW_PITCH_MAX, Math.round(this.map.getPitch() * 10) / 10))
      }
    })
    this.#renderLiveButton()
    this.#renderFollowButton()
    this.#renderPitchButton()
    this.#renderOrientButton()
  }

  stop() {
    this.#cancelCameraIntent()
    clearTimeout(this.resumeTimer)
    this.#stopGeoWatch()
    this.compass?.stop()
    this.listeners.forEach(([event, handler]) => this.map?.off?.(event, handler))
    this.listeners = []
  }

  seedTracking(tracking) {
    this.tracking = !!tracking
  }

  dispatch(event) {
    const previous = this.mode
    const { mode, effects } = cameraModeReducer(previous, event)
    this.mode = mode
    for (const effect of effects) {
      if (effect === "armRecenter") this.#armRecenter()
      else if (effect === "clearRecenter") clearTimeout(this.resumeTimer)
    }
    if (mode !== previous) this.#onModeChange(previous, mode, event)
    this.#renderLiveButton()
    this.#renderFollowButton()
    this.#renderOrientButton()
    return mode
  }

  applyTrackingTransition(plan) {
    this.prepareTrackingTransition(plan)
    this.completeTrackingTransition(plan)
  }

  prepareTrackingTransition(plan) {
    this.tracking = plan.tracking
    if (plan.stopGeoWatch) this.#stopGeoWatch()
    if (plan.zoomedToFollow != null) this.zoomedToFollow = plan.zoomedToFollow
  }

  completeTrackingTransition(plan) {
    if (plan.cameraEvent) this.dispatch({ type: plan.cameraEvent })
    if (plan.renderOrient) this.#renderOrientButton()
  }

  updateTrackingPosition({ lat, lon, heading }) {
    this.last = [lat, lon]
    if (heading != null && heading >= 0) this.course = heading
    this.onLocation({ lat, lon, source: "tracking" })
    const plan = trackingFollowCameraPlan({
      mode: this.mode,
      zoomedToFollow: this.zoomedToFollow,
      lat,
      lon,
      followZoom: this.#followZoom(),
      followPitch: this.#followPitch(),
      bearing: this.#orientBearing()
    })
    if (plan.zoomedToFollow != null) this.zoomedToFollow = plan.zoomedToFollow
    if (plan.move) {
      this.#syncPitchState(plan.move.target.pitch)
      this.#moveCamera(plan.move.target, plan.move.options)
    }
  }

  settingChanged(key) {
    if (key === "followPitch" && this.isLive()) {
      const pitch = this.#followPitch()
      this.pitch3d = pitch > 0
      this.#moveCamera({ pitch }, { duration: 250 })
      this.#renderPitchButton()
    }
    if (key === "followZoom" && this.isLive() && this.last) {
      this.#moveCamera({ center: this.#lngLat(this.last), zoom: this.#followZoom() }, { duration: 250 })
    }
  }

  locate() {
    if (this.mode !== "following") this.dispatch({ type: "RECENTER_SELF" })
    else this.#locate({ live: true })
  }

  live() {
    this.dispatch({ type: "TAP_LIVE" })
  }

  toggleFollow() {
    this.dispatch({ type: this.mode === "following" ? "ORIENT_OFF" : "RECENTER_SELF" })
  }

  async cycleOrient() {
    if (this.tracking && this.mode !== "following") {
      this.dispatch({ type: "RECENTER_SELF" })
      return
    }
    const enabled = ["north", ...(this.compassAvailable() ? ["heading"] : []), "course", "off"]
    let next = nextOrient({ enabled, current: this.orientation, noOff: this.tracking })
    if (next === "heading" && !(await this.compass.start())) {
      next = nextOrient({ enabled, current: "heading", noOff: this.tracking })
    }
    this.#setOrientation(next)
  }

  togglePitch() {
    const plan = pitchTogglePlan({
      currentPitch: this.map.getPitch(),
      lastThreeDimPitch: this.#setting("followPitch3d"),
      defaultPitch: WEB_DEFAULT_SETTINGS.followPitch3d
    })
    this.onSettingsChange(
      { followPitch: plan.pitch, followPitch3d: plan.lastThreeDimPitch },
      { key: "followPitch", value: plan.pitch }
    )
    this.pitch3d = plan.pitch3d
    this.#moveCamera({ pitch: plan.pitch }, { duration: 300 })
    this.#renderPitchButton()
    this.setStatus(this.pitch3d ? "3D view" : "2D view")
  }

  goToExternal({ lat, lon, zoom, hard = false }) {
    if (lat == null || lon == null) return
    this.#releaseCameraForExternalMove()
    this.onForceAutoSearch()
    this.#moveCamera(
      { center: [lon, lat], zoom, pitch: 0 },
      {
        method: hard ? "jumpTo" : "flyTo",
        duration: hard ? 0 : 900,
        correctionDelays: hard ? this.constructor.HARD_CAMERA_CORRECTIONS : this.constructor.CAMERA_CORRECTIONS,
        correctionDuration: hard ? 140 : 180
      }
    )
  }

  isLive() {
    return this.mode === "following"
  }

  #listen(event, handler) {
    this.map.on(event, handler)
    this.listeners.push([event, handler])
  }

  #setting(key) {
    return this.getSetting(key) ?? WEB_DEFAULT_SETTINGS[key]
  }

  #followZoom() {
    const zoom = Number(this.#setting("followZoom"))
    return Number.isFinite(zoom) ? zoom : WEB_DEFAULT_SETTINGS.followZoom
  }

  #followPitch() {
    const pitch = Number(this.#setting("followPitch"))
    return Number.isFinite(pitch) ? pitch : WEB_DEFAULT_SETTINGS.followPitch
  }

  #autoRecenterMs() {
    return Math.max(0, Number(this.#setting("autoRecenterSec")) || 0) * 1000
  }

  #onModeChange(previous, next, event) {
    const followLike = mode => mode === "following" || mode === "paused"
    if (next === "following") this.#engageFollow()
    if (!this.tracking && followLike(previous) && !followLike(next)) this.#stopGeoWatch()
    if (next === "explore" && (event.type === "TAP_LIVE" || event.type === "GESTURE_PANNED")) this.setStatus("Explore")
  }

  #armRecenter() {
    clearTimeout(this.resumeTimer)
    const delay = this.#autoRecenterMs()
    if (delay > 0) this.resumeTimer = setTimeout(() => this.dispatch({ type: "AUTO_RECENTER" }), delay)
  }

  #engageFollow() {
    const pitch = this.#followPitch()
    if (this.tracking) {
      this.zoomedToFollow = false
      if (this.last) {
        this.#syncPitchState(pitch)
        this.#moveCamera(
          { center: this.#lngLat(this.last), zoom: this.#followZoom(), pitch, bearing: this.#orientBearing() ?? undefined },
          { duration: 500 }
        )
        this.zoomedToFollow = true
      } else {
        this.#locate({ live: true })
      }
      this.setStatus("Following")
      return
    }
    this.zoomedToLive = false
    if (this.last) {
      this.#syncPitchState(pitch)
      this.#moveCamera({ center: this.#lngLat(this.last), zoom: this.#followZoom(), pitch }, { duration: 500 })
      this.zoomedToLive = true
    }
    this.#locate({ live: true })
  }

  #cancelCameraIntent() {
    this.cameraIntent?.cancel()
  }

  #moveCamera(target, options = {}) {
    this.cameraIntent?.move(target, { correctionDuration: 180, ...options })
  }

  #syncPitchState(pitch) {
    if (pitch == null) return
    this.pitch3d = Number(pitch) > 0
    this.#renderPitchButton()
  }

  #gestureSnapshot() {
    return {
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      pitch: this.map.getPitch(),
      wasFollowing: this.mode === "following"
    }
  }

  #userMoved() {
    this.#cancelCameraIntent()
    this.dispatch({ type: "GESTURE_START" })
  }

  #classifyGestureEnd(gesture) {
    if (!gesture?.wasFollowing) return
    const end = { center: this.map.getCenter(), zoom: this.map.getZoom(), pitch: this.map.getPitch() }
    const adjustment = gestureAdjustment(gesture, end)
    if (!adjustment) {
      this.dispatch({ type: "GESTURE_PANNED", px: panDistancePx(gesture, end) })
      return
    }
    if (adjustment.zoom != null) this.#saveCameraSetting("followZoom", adjustment.zoom)
    if (adjustment.pitch != null) this.#saveCameraSetting("followPitch", adjustment.pitch)
    this.dispatch({ type: "GESTURE_ADJUSTED" })
  }

  #saveCameraSetting(key, value) {
    const patch = { [key]: value }
    if (key === "followPitch" && Number(value) > 0) patch.followPitch3d = value
    this.onSettingsChange(patch, { key, value })
  }

  #releaseCameraForExternalMove() {
    this.dispatch({ type: "EXTERNAL_RELEASE" })
    this.orientation = "off"
    this.pitch3d = false
    this.#renderOrientButton()
    this.#renderPitchButton()
  }

  #setOrientation(mode) {
    const plan = webOrientationCameraPlan({
      mode: this.mode,
      tracking: this.tracking,
      orientation: mode,
      bearing: bearingForOrient(mode, { course: this.course, deviceHeading: this.compassHeading }),
      lastFix: this.last,
      mapBearing: this.map.getBearing()
    })
    if (!plan.apply) return
    this.orientation = plan.orientation
    if (plan.renderOrient) this.#renderOrientButton()
    if (plan.dispatch) this.dispatch({ type: plan.dispatch })
    if (plan.move) this.#moveCamera(plan.move.target, plan.move.options)
    if (plan.status) this.setStatus(plan.status)
  }

  #orientBearing() {
    return bearingForOrient(this.orientation, { course: this.course, deviceHeading: this.compassHeading })
  }

  #onCompassHeading(heading) {
    this.compassHeading = heading
    if (this.orientation !== "heading" || this.isLive()) return
    const now = Date.now()
    if (this.lastCompassAt && now - this.lastCompassAt < 150) return
    this.lastCompassAt = now
    this.#moveCamera({ bearing: heading }, { duration: 150 })
  }

  #locate({ live = false } = {}) {
    const fallback = 'pan/zoom to your area, then "Search this area".'
    if (!this.geolocation) {
      this.#followFailed(`Geolocation unsupported — ${fallback}`)
      return
    }
    this.setStatus("Finding your location…")
    this.geolocation.getCurrentPosition(
      position => {
        if (!this.isConnected()) return
        if (live && this.mode !== "following") return
        this.showLocate(false)
        this.#onBrowserLocation(position, { center: true })
        if (live && !this.tracking) this.#startGeoWatch()
        if (!this.autoSearchEnabled()) this.search()
      },
      error => {
        if (!this.isConnected()) return
        this.showLocate(true)
        const denied = error.code === error.PERMISSION_DENIED
        this.#followFailed(denied
          ? `Location blocked — allow it via the address-bar icon, or ${fallback}`
          : `Location unavailable — ${fallback}`)
      },
      browserLocationLocateOptions()
    )
  }

  #followFailed(message) {
    if (!this.tracking && this.mode !== "explore") this.dispatch({ type: "EXTERNAL_RELEASE" })
    this.setStatus(message)
  }

  #startGeoWatch() {
    if (this.geoWatchId != null || !this.geolocation) return
    this.geoWatchId = this.geolocation.watchPosition(
      position => this.#onBrowserLocation(position),
      () => {},
      browserLocationWatchOptions()
    )
  }

  #stopGeoWatch() {
    if (this.geoWatchId == null || !this.geolocation) return
    this.geolocation.clearWatch(this.geoWatchId)
    this.geoWatchId = null
  }

  #onBrowserLocation(position, { center = false } = {}) {
    const followLike = this.mode === "following" || this.mode === "paused"
    if (!this.tracking && !followLike) return
    const { latitude: lat, longitude: lon, heading } = position.coords
    this.last = [lat, lon]
    if (heading != null && heading >= 0) this.course = heading
    this.onLocation({ lat, lon, source: "browser" })
    const plan = browserFollowCameraPlan({
      mode: this.mode,
      tracking: this.tracking,
      centerRequested: center,
      zoomedToLive: this.zoomedToLive,
      lat,
      lon,
      followZoom: this.#followZoom(),
      followPitch: this.#followPitch(),
      bearing: this.#orientBearing()
    })
    if (plan.forceNextAutoSearch) this.onForceAutoSearch()
    if (plan.zoomedToLive != null) this.zoomedToLive = plan.zoomedToLive
    if (plan.move) {
      this.#syncPitchState(plan.move.target.pitch)
      this.#moveCamera(plan.move.target, plan.move.options)
    }
  }

  #renderFollowButton() {
    const target = this.controls.follow
    if (!target) return
    const state = followButtonState(this.mode)
    target.classList.toggle("bg-blue-600", state.active)
    target.classList.toggle("text-white", state.active)
    target.classList.toggle("bg-white/95", !state.active)
    target.classList.toggle("text-blue-600", !state.active)
  }

  #renderLiveButton() {
    const target = this.controls.live
    if (!target) return
    const state = liveButtonState(this.mode)
    target.textContent = state.label
    target.classList.toggle("bg-blue-600", state.active)
    target.classList.toggle("text-white", state.active)
    target.classList.toggle("hover:bg-blue-700", state.active)
    target.classList.toggle("bg-white/95", !state.active)
    target.classList.toggle("text-slate-900", !state.active)
    target.classList.toggle("hover:bg-white", !state.active)
  }

  #renderPitchButton() {
    const target = this.controls.pitch
    if (!target) return
    const state = pitchButtonState(this.pitch3d)
    target.textContent = state.label
    target.classList.toggle("bg-blue-600", state.active)
    target.classList.toggle("text-white", state.active)
    target.classList.toggle("hover:bg-blue-700", state.active)
    target.classList.toggle("bg-white/95", !state.active)
    target.classList.toggle("text-slate-800", !state.active)
    target.classList.toggle("hover:bg-white", !state.active)
  }

  #renderOrientButton() {
    const target = this.controls.orient
    if (!target) return
    const state = orientButtonState({ tracking: this.tracking, mode: this.mode, orientation: this.orientation })
    target.textContent = state.label
    target.classList.toggle("text-sm", state.compact)
    target.classList.toggle("bg-blue-600", state.active)
    target.classList.toggle("text-white", state.active)
    target.classList.toggle("hover:bg-blue-700", state.active)
    target.classList.toggle("bg-white/95", !state.active)
    target.classList.toggle("text-slate-800", !state.active)
    target.classList.toggle("hover:bg-white", !state.active)
  }

  #lngLat([lat, lon]) {
    return [lon, lat]
  }
}
